import { createContext, FC, useContext, useMemo } from 'react'
import { PropsWithChildren } from 'react'
import { useNavigate } from 'react-router-dom'

import { ID } from '@models/base'
import { ClinicModel } from '@models/clinic'

import { useAuth } from '@modules/auth/contexts/AuthContext'

import StorageUtil, { StorageKeyEnum } from '@/utils/StorageUtil'

type ActiveClinicContextProps = {
  activeClinic?: ClinicModel
  setActiveClinic: (clinicId: ID) => void
}

const ActiveClinicContext = createContext<ActiveClinicContextProps>({
  setActiveClinic: () => null,
})

const useActiveClinic = () => useContext(ActiveClinicContext)

const ActiveClinicProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const activeClinic = useMemo<ClinicModel | undefined>(() => {
    return currentUser?.clinics?.find((clinic) => clinic.id === currentUser.active_clinic_id)
  }, [currentUser?.active_clinic_id, currentUser?.clinics])

  const setActiveClinic = (clinicId: ID) => {
    if (clinicId) {
      StorageUtil.set(StorageKeyEnum.ACTIVE_CLINIC_ID, clinicId)
      navigate(window.location.pathname, { replace: true })
      window.location.reload()
    }
  }

  return (
    <ActiveClinicContext.Provider value={{ activeClinic, setActiveClinic }}>{children}</ActiveClinicContext.Provider>
  )
}

export { ActiveClinicProvider, useActiveClinic }
